import React from 'react';
import { Button } from './ui/button';
import { Award, Users, Clock, MapPin } from 'lucide-react';
import { OrderButton } from './OrderButton';

export const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative bg-gradient-to-br from-blue-50 to-sky-100 min-h-screen flex items-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-500">
                  No Minimums & Unlimited
                </span>{' '}
                Print and Embroidery options.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-blue-600">
                  Let's craft the vision for your legacy brand today!
                </span>
              </h1>
              <p className="mt-6 text-xl text-gray-700 leading-relaxed">
                Custom printed and embroidered apparel that brings your brand to life. 
                From t-shirts to mugs, we help small businesses make their mark with 
                durable, stylish, and high-quality branded products.
              </p>

              {/* Now Serving Badge */}
              <div
                data-testid="now-serving-badge"
                className="mt-6 inline-flex items-center gap-3 bg-white/70 backdrop-blur-sm border border-teal-200 rounded-full pl-4 pr-5 py-2 shadow-sm"
              >
                <span className="flex items-center justify-center bg-gradient-to-br from-blue-600 to-sky-500 text-white rounded-full p-1.5">
                  <MapPin className="h-4 w-4" />
                </span>
                <p className="text-sm sm:text-base text-gray-800">
                  <span className="font-semibold text-blue-700">Now Serving:</span>{' '}
                  Orangeville, Newmarket, Hamilton &amp; surrounding areas
                </p>
              </div>

              {/* Top Order CTA — directly under service areas */}
              <div className="mt-6">
                <OrderButton
                  label="Start Your Order"
                  testid="hero-top-start-order-button"
                  className="px-8 py-4 text-lg"
                />
              </div>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Award className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">No Minimum</p>
                  <p className="text-sm text-gray-600">Order Policy</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-sky-100 p-2 rounded-lg">
                  <Users className="h-6 w-6 text-sky-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Free Sample</p>
                  <p className="text-sm text-gray-600">With Your Logo</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-teal-100 p-2 rounded-lg">
                  <Clock className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Quick</p>
                  <p className="text-sm text-gray-600">Turnaround</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => window.open("https://mail.google.com/mail/?view=cm&fs=1&to=communications@da-12.com&su=Consultation Request — Which location? Orangeville / Newmarket / Hamilton&body=Hi Avid Branding,%0D%0A%0D%0AI'd like to book a free consultation. Please let me know the next steps — a branding advisor will be in touch.%0D%0A%0D%0APreferred consultation location (choose one): Orangeville / Newmarket / Hamilton%0D%0A%0D%0AName:%0D%0APhone:%0D%0ACompany:%0D%0AProject Details:%0D%0A%0D%0AThank you!", '_blank')}
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold transition-all duration-200"
              >
                Email Us for Free Consultation
              </Button>
              <Button 
                onClick={() => window.open('https://mail.google.com/mail/?view=cm&fs=1&to=communications@da-12.com&su=Free Sample Request', '_blank')}
                variant="outline"
                size="lg"
                className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-4 text-lg font-semibold transition-all duration-200"
              >
                Email Us for Free Sample
              </Button>
            </div>

            {/* Newsletter Signup */}
            <div className="mt-6">
              <Button 
                onClick={() => window.open('https://preview.mailerlite.io/forms/1300492/164127746890400924/share', '_blank')}
                variant="outline"
                size="lg"
                className="border-2 border-teal-500 text-teal-600 hover:bg-teal-50 px-8 py-4 text-lg font-semibold transition-all duration-200"
              >
                📧 Subscribe to our mailout
              </Button>
            </div>

            {/* Contact Info */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-blue-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold text-gray-900">Call Us Today</p>
                  <p className="text-blue-600 font-bold text-lg">519-938-8959</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Visit Our Office</p>
                  <p className="text-gray-700">4-246372 Hockley Rd</p>
                  <p className="text-gray-700">Orangeville, ON</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative">
            <div className="relative bg-white p-8 rounded-2xl shadow-xl border border-blue-100">
              <img 
                src="https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/i2o3vv4y_AVIDBRANDING-GOOD-VIBES-ONLY-HELPFUL-HEARTFUL-SERVICE-ORANGEVILLE.png" 
                alt="Avid Branding - Bring me your what ifs and we'll turn them into heck yeahs - Good vibes only, helpful heartful service in Orangeville"
                className="w-full aspect-square object-cover rounded-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-teal-500 text-white px-4 py-2 rounded-lg shadow-lg">
                <p className="font-bold">Quality Guaranteed</p>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-8 -left-8 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg transform rotate-3">
              <p className="font-bold">Since 2022</p>
            </div>
            <div className="absolute -bottom-8 left-8 bg-sky-500 text-white px-6 py-3 rounded-lg shadow-lg transform -rotate-2">
              <p className="font-bold">Local Experts</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};